import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  Checkbox,
  ListItemButton,
  Tooltip,
  Grid,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import DeselectIcon from '@mui/icons-material/Deselect';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UndoIcon from '@mui/icons-material/Undo';
import PhoneIcon from '@mui/icons-material/Phone';
import HomeIcon from '@mui/icons-material/Home';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import EventIcon from '@mui/icons-material/Event';
import DescriptionIcon from '@mui/icons-material/Description';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import HealingIcon from '@mui/icons-material/Healing';
import BiotechIcon from '@mui/icons-material/Biotech';
import ScienceIcon from '@mui/icons-material/Science';
import BloodtypeIcon from '@mui/icons-material/Bloodtype';

const CATEGORY_META = {
  contacts: { Icon: PhoneIcon, color: '#0288d1' },
  addresses: { Icon: HomeIcon, color: '#7b1fa2' },
  referrals: { Icon: ForwardToInboxIcon, color: '#1976d2' },
  appointments: { Icon: EventIcon, color: '#f57c00' },
  clinicalNotes: { Icon: DescriptionIcon, color: '#455a64' },
  consents: { Icon: VerifiedUserIcon, color: '#388e3c' },
  treatments: { Icon: HealingIcon, color: '#c2185b' },
  tissuePathology: { Icon: BiotechIcon, color: '#5d4037' },
  samples: { Icon: ScienceIcon, color: '#00796b' },
  tests: { Icon: BloodtypeIcon, color: '#d32f2f' }
};

const getItemId = (item) => item.id || String(item._id);

const getItemLabel = (item) =>
  item.name ||
  item.symbol ||
  item.referralTo ||
  item.contactType ||
  item.addressLine1 ||
  item.appointmentType ||
  item.sampleType ||
  item.testName ||
  item.treatment ||
  item.tissueSite ||
  item.morphology ||
  item.consentType ||
  item.note ||
  String(item._id);

const getItemDate = (item) =>
  item.dateRecorded ||
  item.referralDate ||
  item.contactDate ||
  item.appointmentDate ||
  item.dateTaken ||
  item.collectionDate ||
  item.consentDate ||
  item.treatmentDate ||
  item.pathReportDate ||
  item.dateRequested ||
  item.dateOfBirth ||
  '';

const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const formatTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit'
  });
};

// ---- Category match configuration ------------------------------------------
// Each entry defines: matchKeyField (field to match on), matchKeyLabel,
// and compareFields (fields shown in side-by-side comparison).
const CATEGORY_MATCH_CONFIG = {
  tests: {
    matchKeyField: 'testName',
    matchKeyLabel: 'Test Name',
    subtitle: 'Same test on both persons',
    compareFields: [
      { key: 'testType', label: 'Type' },
      { key: 'dateRequested', label: 'Date Requested', isDate: true },
      { key: 'dateResultDue', label: 'Date Due', isDate: true },
      { key: 'dateResultReceived', label: 'Date Received', isDate: true },
      { key: 'lab', label: 'Lab' },
      { key: 'ageWhenTested', label: 'Age When Tested' },
      { key: 'testResult', label: 'Result' },
      { key: 'testNotes', label: 'Notes' }
    ]
  },
  appointments: {
    matchKeyField: 'clinicName',
    matchKeyLabel: 'Clinic Name',
    subtitle: 'Same clinic on both persons',
    compareFields: [
      { key: 'appointmentDate', label: 'Date', isDate: true },
      { key: 'appointmentTime', label: 'Time', isTime: true },
      { key: 'appointmentType', label: 'Type' },
      { key: 'clinician', label: 'Clinician' },
      { key: 'appointmentStatus', label: 'Status' },
      { key: 'statusReason', label: 'Status reason' },
      { key: 'location', label: 'Location' },
      { key: 'appointmentNotes', label: 'Notes' }
    ]
  },
  clinicalNotes: {
    matchKeyField: 'clinician',
    matchKeyLabel: 'Clinician',
    subtitle: 'Same clinician on both persons',
    compareFields: [
      { key: 'clinicalNoteDate', label: 'Date' },
      { key: 'note', label: 'Note' },
      { key: 'caution', label: 'Caution' }
    ]
  },
  consents: {
    matchKeyField: 'consentType',
    matchKeyLabel: 'Consent Type',
    subtitle: 'Same consent type on both persons',
    compareFields: [
      { key: 'consentDate', label: 'Consent Date', isDate: true },
      { key: 'consentRequestDate', label: 'Request Date', isDate: true },
      { key: 'consentGivenBy', label: 'Given By' },
      { key: 'response', label: 'Response' },
      { key: 'consentNote', label: 'Notes' }
    ]
  },
  samples: {
    matchKeyField: 'sampleType',
    matchKeyLabel: 'Sample Type',
    subtitle: 'Same sample type on both persons',
    compareFields: [
      { key: 'dateTaken', label: 'Date Taken', isDate: true },
      { key: 'sampleHeldBy', label: 'Held By' },
      { key: 'trackingId', label: 'Tracking ID' },
      { key: 'sampleStatus', label: 'Status' },
      { key: 'statusDate', label: 'Status Date', isDate: true },
      { key: 'sampleNotes', label: 'Notes' }
    ]
  },
  referrals: {
    matchKeyField: 'referralTo',
    matchKeyLabel: 'Referral To',
    subtitle: 'Same referral target on both persons',
    compareFields: [
      { key: 'referralType', label: 'Type' },
      { key: 'referralDate', label: 'Date', isDate: true },
      { key: 'referredBy', label: 'Referred By' },
      { key: 'referralReason', label: 'Reason' },
      { key: 'referralTriage', label: 'Triage' },
      { key: 'referralStatus', label: 'Status' },
      { key: 'referralNotes', label: 'Notes' },
      { key: 'dateClosed', label: 'Date Closed', isDate: true }
    ]
  },
  treatments: {
    matchKeyField: 'treatment',
    matchKeyLabel: 'Treatment',
    subtitle: 'Same treatment on both persons',
    compareFields: [
      { key: 'treatmentDate', label: 'Start Date', isDate: true },
      { key: 'treatmentEndDate', label: 'End Date', isDate: true },
      { key: 'comment', label: 'Comment' }
    ]
  }
};

const fmtVal = (value, isDate, isTime) => {
  if (value === undefined || value === null || value === '') return '—';
  if (isDate) return formatDate(value) || '—';
  if (isTime) return formatTime(value) || '—';
  return String(value);
};

/** Side-by-side comparison row for a single retained↔deleted record pair.
 *  Uses MUI Accordion for consistency with the rest of the app. */
function MatchingRecordRow({
  matchLabel,
  subtitle,
  compareFields,
  retainedRecord,
  deletedRecord,
  overrides,
  onOverride,
  color
}) {
  const retainedId = getItemId(retainedRecord);
  const currentOverrides = overrides[retainedId] || {};
  const adoptedCount = Object.keys(currentOverrides).length;

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        '&:before': { display: 'none' },
        border: 'none',
        backgroundColor: 'transparent'
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 2,
          minHeight: 48,
          '& .MuiAccordionSummary-content': {
            my: 1,
            alignItems: 'center',
            gap: 1
          }
        }}
      >
        <Avatar
          sx={{
            width: 30,
            height: 30,
            bgcolor: `${color}1a`,
            color,
            mr: 0.5
          }}
        >
          <CompareArrowsIcon sx={{ fontSize: 14 }} />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, lineHeight: 1.35 }}
            noWrap
          >
            {matchLabel}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              lineHeight: 1.3
            }}
          >
            {subtitle}
          </Typography>
        </Box>
        {adoptedCount > 0 && (
          <Chip
            label={`${adoptedCount} field(s) adopted`}
            size="small"
            color="success"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: 11 }}
          />
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
        <Paper
          variant="outlined"
          sx={{ borderRadius: 1.5, overflow: 'hidden' }}
        >
          {/* Column headers */}
          <Grid
            container
            sx={{
              backgroundColor: '#fafafa',
              borderBottom: '1px solid',
              borderColor: 'divider',
              px: 2,
              py: 1
            }}
          >
            <Grid size={3}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: 'text.secondary'
                }}
              >
                Field
              </Typography>
            </Grid>
            <Grid size={4.5}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 13, color: 'success.main' }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: 'success.dark'
                  }}
                >
                  Retained
                </Typography>
              </Box>
            </Grid>
            <Grid size={4.5}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 13, color: 'error.main' }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: 'error.dark'
                  }}
                >
                  Deleted
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Field rows — skip fields empty on both sides */}
          {compareFields
            .filter((field) => {
              const retRaw =
                currentOverrides[field.key] !== undefined
                  ? currentOverrides[field.key]
                  : retainedRecord[field.key];
              const delRaw = deletedRecord[field.key];
              const isEmpty = (v) => v === undefined || v === null || v === '';
              return !(isEmpty(retRaw) && isEmpty(delRaw));
            })
            .map((field, idx, visibleFields) => {
              const rawRetVal =
                currentOverrides[field.key] !== undefined
                  ? currentOverrides[field.key]
                  : retainedRecord[field.key];
              const retVal = fmtVal(rawRetVal, field.isDate);
              const delVal = fmtVal(deletedRecord[field.key], field.isDate);
              const differs = retVal !== delVal;
              const isAdopted = currentOverrides[field.key] !== undefined;
              const deletedHasValue =
                deletedRecord[field.key] !== undefined &&
                deletedRecord[field.key] !== null &&
                deletedRecord[field.key] !== '';

              return (
                <Grid
                  container
                  key={field.key}
                  sx={{
                    px: 2,
                    py: 1,
                    alignItems: 'center',
                    ...(idx < visibleFields.length - 1 && {
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    }),
                    ...(isAdopted && {
                      backgroundColor: 'rgba(46, 125, 50, 0.05)'
                    })
                  }}
                >
                  <Grid size={3}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: 'text.secondary',
                        fontSize: 13
                      }}
                    >
                      {field.label}
                    </Typography>
                  </Grid>
                  <Grid size={4.5}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: 13,
                          ...(isAdopted && {
                            color: 'success.main',
                            fontWeight: 600
                          })
                        }}
                      >
                        {retVal}
                      </Typography>
                      {isAdopted && (
                        <Tooltip title="Revert to original retained value">
                          <IconButton
                            size="small"
                            sx={{ p: 0.25 }}
                            onClick={() =>
                              onOverride(retainedId, field.key, undefined)
                            }
                          >
                            <UndoIcon
                              sx={{ fontSize: 14, color: 'warning.main' }}
                            />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Grid>
                  <Grid size={4.5}>
                    {deletedHasValue && differs && !isAdopted ? (
                      <Tooltip title="Click to adopt this value into retained record">
                        <Typography
                          variant="body2"
                          onClick={() =>
                            onOverride(
                              retainedId,
                              field.key,
                              deletedRecord[field.key]
                            )
                          }
                          sx={{
                            fontSize: 13,
                            color: 'primary.main',
                            fontWeight: 500,
                            cursor: 'pointer',
                            display: 'inline-block',
                            borderBottom: '1px dashed',
                            borderColor: 'primary.light',
                            '&:hover': {
                              color: 'success.main',
                              borderColor: 'success.main'
                            }
                          }}
                        >
                          {delVal}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: 13,
                          ...(isAdopted && {
                            textDecoration: 'line-through',
                            color: 'text.disabled'
                          }),
                          ...(!isAdopted &&
                            differs && { color: 'text.secondary' })
                        }}
                      >
                        {delVal}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              );
            })}
        </Paper>
      </AccordionDetails>
    </Accordion>
  );
}

function RecordRow({ item, color, checkable, checked, onToggle, divider }) {
  const id = getItemId(item);
  const label = getItemLabel(item);
  const date = getItemDate(item);

  const avatar = (
    <ListItemAvatar sx={{ minWidth: 40 }}>
      <Avatar
        sx={{
          width: 30,
          height: 30,
          bgcolor: `${color}1a`,
          color
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 700 }}>
          {(label || '?').charAt(0).toUpperCase()}
        </span>
      </Avatar>
    </ListItemAvatar>
  );

  const content = (
    <Box
      sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}
    >
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, lineHeight: 1.35 }}
        noWrap
      >
        {label || '—'}
      </Typography>
      {date && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            mt: 0.25,
            lineHeight: 1.3
          }}
        >
          {formatDate(date)}
        </Typography>
      )}
    </Box>
  );

  const rowSx = {
    py: 2.5,
    px: 2,
    gap: 0.5,
    ...(divider && {
      borderBottom: '1px solid',
      borderColor: 'divider'
    })
  };

  if (!checkable) {
    return (
      <ListItem key={id} sx={rowSx}>
        {avatar}
        {content}
      </ListItem>
    );
  }

  return (
    <ListItemButton
      key={id}
      onClick={() => onToggle?.(id)}
      sx={{
        ...rowSx,
        backgroundColor: checked ? 'rgba(46, 125, 50, 0.08)' : 'inherit',
        '&:hover': {
          backgroundColor: checked
            ? 'rgba(46, 125, 50, 0.12)'
            : 'rgba(0, 0, 0, 0.04)'
        }
      }}
    >
      {avatar}
      {content}
      <Checkbox
        checked={checked}
        size="small"
        color="success"
        edge="end"
        tabIndex={-1}
        sx={{ ml: 0.5 }}
      />
    </ListItemButton>
  );
}

function CategorySection({
  section,
  onToggle,
  onSelectAll,
  testFieldOverrides,
  onTestFieldOverride
}) {
  const { key, label, retained, deleted, selected } = section;
  const { Icon, color } = CATEGORY_META[key] || {
    Icon: DescriptionIcon,
    color: '#666'
  };

  // Detect matching records between retained and deleted based on category config
  const matchConfig = CATEGORY_MATCH_CONFIG[key];
  const { matchingRecords, matchedDeletedIds, matchedRetainedIds } =
    React.useMemo(() => {
      if (!matchConfig)
        return {
          matchingRecords: [],
          matchedDeletedIds: new Set(),
          matchedRetainedIds: new Set()
        };
      const { matchKeyField } = matchConfig;
      const retainedByKey = new Map();
      retained.forEach((item) => {
        const name = (item[matchKeyField] || '').trim().toLowerCase();
        if (name) {
          if (!retainedByKey.has(name)) retainedByKey.set(name, []);
          retainedByKey.get(name).push(item);
        }
      });
      const matches = [];
      const mIds = new Set();
      const rIds = new Set();
      const seenNames = new Set();
      deleted.forEach((item) => {
        const name = (item[matchKeyField] || '').trim().toLowerCase();
        if (!name || !retainedByKey.has(name)) return;
        mIds.add(getItemId(item));
        if (seenNames.has(name)) return;
        seenNames.add(name);
        const deletedWithName = deleted.filter(
          (d) => (d[matchKeyField] || '').trim().toLowerCase() === name
        );
        // Pair each retained record with the corresponding deleted record (1:1 pairing)
        const retItems = retainedByKey.get(name);
        retItems.forEach((ret, idx) => {
          const del = deletedWithName[idx] || deletedWithName[0];
          rIds.add(getItemId(ret));
          matches.push({
            matchLabel: item[matchKeyField],
            retainedRecord: ret,
            deletedRecord: del
          });
        });
      });
      return {
        matchingRecords: matches,
        matchedDeletedIds: mIds,
        matchedRetainedIds: rIds
      };
    }, [key, retained, deleted, matchConfig]);

  // Filter out matched records from the normal retained/transfer lists
  const unmatchedRetained = matchConfig
    ? retained.filter((item) => !matchedRetainedIds.has(getItemId(item)))
    : retained;
  const unmatchedDeleted = matchConfig
    ? deleted.filter((item) => !matchedDeletedIds.has(getItemId(item)))
    : deleted;

  const allSelected =
    unmatchedDeleted.length > 0 &&
    unmatchedDeleted.every((item) => selected.includes(getItemId(item)));

  const emptyState = (text) => (
    <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
      <Typography
        variant="caption"
        sx={{
          color: 'text.disabled',
          fontStyle: 'italic'
        }}
      >
        {text}
      </Typography>
    </Box>
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        mb: 3.5,
        overflow: 'hidden',
        borderColor: 'divider',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
      }}
    >
      {/* Section header */}
      <Box
        sx={{
          px: 2,
          py: 2,
          backgroundColor: '#fafafa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
          gap: 1
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            minWidth: 0
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: `${color}1f`,
              color
            }}
          >
            <Icon sx={{ fontSize: 18 }} />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2
              }}
            >
              {label}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary'
              }}
            >
              {unmatchedRetained.length} retained · {unmatchedDeleted.length} on
              deleted
              {matchedDeletedIds.size > 0 &&
                ` · ${matchedDeletedIds.size} matched by ${
                  matchConfig?.matchKeyLabel || 'name'
                }`}
            </Typography>
          </Box>
        </Box>
        {unmatchedDeleted.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexShrink: 0
            }}
          >
            <Chip
              label={`${
                selected.filter((id) => !matchedDeletedIds.has(id)).length
              } / ${unmatchedDeleted.length} to transfer`}
              size="small"
              color={selected.length > 0 ? 'success' : 'default'}
              variant={selected.length > 0 ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600 }}
            />
            <Tooltip
              title={
                allSelected
                  ? 'Clear all transfers in this section'
                  : 'Transfer every record in this section'
              }
            >
              <Button
                size="small"
                variant="outlined"
                startIcon={allSelected ? <DeselectIcon /> : <SelectAllIcon />}
                onClick={() =>
                  allSelected
                    ? onSelectAll(key, [])
                    : onSelectAll(key, unmatchedDeleted.map(getItemId))
                }
                sx={{ textTransform: 'none' }}
              >
                {allSelected ? 'Clear' : 'Transfer All'}
              </Button>
            </Tooltip>
          </Box>
        )}
      </Box>
      {/* Retained person's records — only unmatched retained records */}
      {unmatchedRetained.length > 0 && (
        <Box
          sx={{
            borderBottom: deleted.length > 0 ? '1px solid' : 'none',
            borderColor: 'divider'
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 0.85,
              backgroundColor: 'rgba(46, 125, 50, 0.06)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}
          >
            <CheckCircleOutlineIcon
              sx={{ fontSize: 15, color: 'text.secondary' }}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: 'success.dark'
              }}
            >
              Retained ({unmatchedRetained.length})
            </Typography>
          </Box>
          <List dense disablePadding>
            {unmatchedRetained.map((item, idx) => (
              <RecordRow
                key={getItemId(item)}
                item={item}
                color={color}
                checkable={false}
                divider={idx < unmatchedRetained.length - 1}
              />
            ))}
          </List>
        </Box>
      )}
      {/* Deleted person's records — only unmatched ones */}
      {unmatchedDeleted.length > 0 && (
        <Box>
          <Box
            sx={{
              px: 2,
              py: 0.85,
              backgroundColor: 'rgba(211, 47, 47, 0.06)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: 'error.dark'
              }}
            >
              Deleted ({unmatchedDeleted.length})
            </Typography>
          </Box>
          <List dense disablePadding>
            {unmatchedDeleted.map((item, idx) => {
              const id = getItemId(item);
              return (
                <RecordRow
                  key={id}
                  item={item}
                  color={color}
                  checkable
                  checked={selected.includes(id)}
                  onToggle={(rowId) => onToggle(key, rowId)}
                  divider={idx < unmatchedDeleted.length - 1}
                />
              );
            })}
          </List>
        </Box>
      )}
      {/* Matching record comparison rows — styled like Retained/Deleted rows */}
      {matchingRecords.length > 0 && (
        <Box>
          <Box
            sx={{
              px: 2,
              py: 0.85,
              backgroundColor: 'rgba(2, 136, 209, 0.06)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}
          >
            <CompareArrowsIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: 'info.dark'
              }}
            >
              Matched Records ({matchingRecords.length})
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                ml: 0.5
              }}
            >
              — click deleted values to adopt into retained record
            </Typography>
          </Box>
          <List dense disablePadding>
            {matchingRecords.map((match, idx) => (
              <ListItem
                key={getItemId(match.retainedRecord)}
                sx={{
                  p: 0,
                  display: 'block',
                  ...(idx < matchingRecords.length - 1 && {
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  })
                }}
              >
                <MatchingRecordRow
                  matchLabel={match.matchLabel}
                  subtitle={matchConfig.subtitle}
                  compareFields={matchConfig.compareFields}
                  retainedRecord={match.retainedRecord}
                  deletedRecord={match.deletedRecord}
                  overrides={testFieldOverrides || {}}
                  onOverride={onTestFieldOverride}
                  color={color}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Paper>
  );
}

export default function ClinicalRecordsStep({
  sections,
  retainedName,
  deletedName,
  onToggle,
  onSelectAll,
  testFieldOverrides,
  onTestFieldOverride
}) {
  if (!sections || sections.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="body2">
          No clinical records to merge — neither person has any.
        </Typography>
      </Box>
    );
  }

  const hasAnyDeletedRecords = sections.some((s) => s.deleted.length > 0);

  return (
    <Box>
      {hasAnyDeletedRecords && (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 2
          }}
        >
          Retained person&apos;s records are preserved automatically. Tick the
          deleted person&apos;s records to transfer them; unticked records will
          be discarded.
        </Typography>
      )}
      {hasAnyDeletedRecords ? (
        <>
          {sections.map((section) => (
            <CategorySection
              key={section.key}
              section={section}
              onToggle={onToggle}
              onSelectAll={onSelectAll}
              testFieldOverrides={testFieldOverrides}
              onTestFieldOverride={onTestFieldOverride}
            />
          ))}
        </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            py: 4,
            textAlign: 'center'
          }}
        >
          <CheckCircleOutlineIcon
            sx={{ color: 'success.main', fontSize: 28 }}
          />
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: 'success.dark'
              }}
            >
              No conflicts to resolve
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mt: 0.25
              }}
            >
              The deleted person has no clinical records. All retained records
              are preserved automatically.
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}
